import React, { useEffect, useState } from 'react';

import moment from "moment";
import { DatePicker } from 'antd';
import { find, has, size, toLower } from 'lodash';
import { BorderedField, Box, Flex } from '../../components/Flex';
import { Label, Paragraph } from '../../components/Text';
import { Input, InputFile, InputPassword } from '../../components/Input';
import { Checkbox } from '../../components/Checkbox';
import { Select } from '../../components/Select';
import { FormTooltip } from '../../components/Tooltip';
import { TextButton, Button } from '../../components/Button';
import { CalendIcon } from '../../icons/CalendIcon';
import { ExplainIcon } from '../../icons/ExplainIcon';
import { PlusIcon } from '../../icons/PlusIcon';
import { RemoveIcon } from '../../icons/RemoveIcon';
import { fixPackageSize } from '../../utils';
import { CancelIcon } from '../../icons/CancelIcon';

const { Option } = Select;

const ActionFormItem = ({
    type,
    name,
    label,
    value,
    options=[],
    onValueChange,
    error=false,
    max,
    disabled,
    uppercase,
    search,
    selectWidth,
    extraLabel,
    loading,
    multiple,
    mode,
    validateValue,
    fields,
    render,
    maxWidth,
    requiredField,
    validateValues,
    showSize,
    rows
 }) => {
    const complexField = Array.isArray(fields) && fields.length;
    const initialState = {
        visible: false,
        value: null,
        error: false,
        disabled: null
    }
    const [ state, setState ] = useState(initialState);

    useEffect(() => {
        if (complexField && state.visible) {
            let values = {}, disabled = {};
            fields.map(field => {
                values[field.key] = null;
            });
            fields.map(field => {
                disabled[field.key] = typeof field.disabled === "function" ? field.disabled(state.value) : state.disabled;
            });
            setState(state => ({ ...state, value: values, disabled: disabled }))
        }
    }, [state.visible, complexField])

    useEffect(() => {
        if (complexField && state.visible) {
            let disabled = {};
            fields.map(field => {
                let ruler = find(fields, { dependencies: field.key});

                disabled[field.key] = (ruler && has(ruler, "key") && has(state.value, ruler.key) && has(state.error, ruler.key) && state.error[ruler.key] !== false)
                    || (typeof field.disabled === "function"
                        ? field.disabled(state.value)
                        : field.disabled);
            });
            setState(state => ({ ...state, disabled: disabled }))
        }
    }, [complexField, state.value])

    const onChange = (value, key) => {
        let newValue = key ? { [key]: value } : value;

        if (value === null && complexField) {
            // clear dependent field if exists
            let dependencies = find(fields, { key: key }).dependencies;
            if (dependencies) {
                newValue[dependencies] = null;
            }
        }
        if (multiple) {
            if (value !== state.value) {
                let err = validateValue(name, value, key, state.value);
                setState(state => ({
                    ...state,
                    value: key
                        ? { ...state.value, ...newValue }
                        : value, error: key
                            ? { [key]: err }
                            : err
                    })
                );
            }
        } else {
            onValueChange(name, value);
        }
    };

    const renderField = ({ type, placeholder, key, error, hasError, value, disabled }) => {
        return (type === "file"
        ? <InputFile
            key={key}
            error={error}
            bordered={false}
            name="file"
            selected={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.files[0], key)}
            onCancel={() => onChange(null, key)}
        />
        : <BorderedField hasError={hasError} key={key}>
            {type === "select" &&
            <Select
                allowClear
                mode={mode}
                bordered={false}
                loading={loading}
                disabled={disabled}
                dropdownMatchSelectWidth={selectWidth}
                showSearch={search}
                value={value}
                onChange={(value) => onChange(value === undefined ? null : value)}
                filterOption={(input, option) =>
                    toLower(option.value).indexOf(toLower(input)) >= 0 ||
                    toLower(option.children).indexOf(toLower(input)) >= 0
                }
            >
                {options.map(item => (
                    <Option key={item.key}>{item.title}</Option>
                ))}
            </Select>}
            {type === "password" &&
            <InputPassword
                bordered={false}
                value={value}
                maxLength={max}
                disabled={disabled}
                placeholder={placeholder}
                onChange={e => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value, key)}
            />}
            {type === "input" &&
            <Input
                bordered={false}
                value={value}
                maxLength={max}
                uppercase={uppercase}
                disabled={disabled}
                placeholder={placeholder}
                onChange={e => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value || null, key)}
            />}
            {type === "text" &&
            <Input.TextArea
                bordered={false}
                value={value}
                disabled={disabled}
                maxLength={max}
                rows={rows}
                onChange={e => onChange(e.target.value)}
            />}
            {type === "date" &&
            <DatePicker
                allowClear={!requiredField}
                bordered={false}
                disabled={disabled}
                value={value && toLower(value) !== "none" && moment(value)}
                suffixIcon={<CalendIcon />}
                style={{ width: "100%" }}
                onChange={(date, dateString) => onChange(dateString || null)}
                clearIcon={<CancelIcon />}
            />
            }
            {error &&
            <>
                <FormTooltip text={error}>
                    <ExplainIcon size={16} style={{marginRight: "10px"}} />
                </FormTooltip>
            </>}
        </BorderedField>)
    };

    const onMultipleValueChange = (val, idx) => {
        validateValues(fields, val)
            .then(success => {
                let currentValues = Array.isArray(value) ? value.slice() : [];
                if (val === null && idx > -1) {
                    currentValues.splice(idx, 1);
                } else {
                    currentValues.push(typeof render === "function"
                        ? render(val)
                        : multiple && name !== "files"
                            ? { name: val }
                            : val
                    );
                }
                onValueChange(name, currentValues);
                setState(initialState);
            })
            .catch(error => {
                setState(state => ({ ...state, error: error }));
            })
    };

    return(
        type === "checkbox"
            ? <Flex>
                <Checkbox checked={value == 1} onChange={e => onChange(e.target.checked)} disabled={disabled}>{label}</Checkbox>
            </Flex>
            : multiple
                ? <>
                    {state.visible
                        ? <>
                            {complexField
                                ? fields.map((f) => renderField({
                                        ...f,
                                        hasError: has(state.error, f.key) && state.error[f.key],
                                        error: has(state.error, f.key) && state.error[f.key],
                                        value: has(state.value, f.key) && state.value[f.key],
                                        disabled: has(state.disabled, f.key) ? state.disabled[f.key] : false
                                    }))
                                : renderField({ type: type, error: state.error, value: state.value })}
                            <Flex justify="flex-end" align="center" gap={{ right: 37 }}>
                                <Button link onClick={() => setState(initialState)}>Cancel</Button>
                                <Button
                                    disabled={(size(state.value) ? Object.values(state.value).every(o => o === null) : !state.value) || !Object.values(state.error).every(o => o === false)}
                                    onClick={() => onMultipleValueChange(state.value)}
                                >
                                    Add
                                </Button>
                            </Flex>
                        </>
                        : <Box flatten error={error} background="#F0F4FB" height="54px" width="100%" align="center" justify="center" margin={{ bottom: "12px" }}>
                            <TextButton disabled={disabled} onClick={() => setState({ ...initialState, visible: true })}>
                                <PlusIcon style={{ marginRight: "8px" }} />
                                Add {label}
                            </TextButton>
                        </Box>}
                    {Array.isArray(value)
                        ? value.map((val, idx) => (
                            <Box key={`multiple-field-${idx}`} flatten direction="row" background="#F0F4FB" width="100%" align="center" justify="space-between" margin={{ bottom: idx === value.length - 1 ? 0 : "12px" }}>
                                <Flex direction="column">
                                    <Paragraph maxWidth={maxWidth} title={val.saveAs || val.name || val.file.name } nowrap>{val.saveAs || val.name || val.file.name || "?"} {showSize && "(" + fixPackageSize(val.file.size) + ")"}</Paragraph>
                                    {has(val, "md5") && <Paragraph bold size="12px">{val["md5"]}</Paragraph>}
                                </Flex>
                                {!disabled &&
                                <RemoveIcon
                                    onClick={() => {
                                        let currentValues = value.slice();
                                        currentValues.splice(idx, 1);
                                        onValueChange(name, currentValues);
                                    }}
                                    color="var(--sp-danger-color)"
                                    hoverColor="var(--sp-danger-hover-color)" />
                                }
                            </Box>))
                        : null}
                </>
            : <Flex direction="column" flex={1}>
                <Flex justify="space-between" margin={{ bottom: "4px"}}>
                    <Flex align="center" gap={{ right: 4 }}>
                        <Label name="form-item-label-text" required={requiredField}>{label}</Label>
                        {error &&
                        <>
                            <FormTooltip text={error}>
                                <ExplainIcon size={16} />
                            </FormTooltip>
                        </>}
                    </Flex>
                    {extraLabel}
                </Flex>
                {renderField({ type: type, value: value, hasError: typeof error === "string", disabled: disabled })}
            </Flex>
    );
};

export { ActionFormItem };